import {
    SignalDataEndpoint,
    SignalsEndpoints,
} from "components/Signals/SignalsModels";
import { EvalType } from "models/Evaluation";
import type { GridRowId } from "@mui/x-data-grid-pro";
import {
    DrilldownBehavior,
    DrilldownCapability,
    DrilldownSource,
    type SelectedDataPoint,
    ViewDataClassification,
} from "./DrilldownTypes";

export const resolveAggregationDrilldownTitle = ({
    selectedDataPoint,
    aggregationLabels = [],
    fallbackTitle,
}: {
    selectedDataPoint: SelectedDataPoint | null;
    aggregationLabels?: string[];
    fallbackTitle?: string;
}): string => {
    const selectedTheme = [
        selectedDataPoint?.x,
        selectedDataPoint?.series,
    ].find(
        (value) =>
            value !== undefined &&
            value !== null &&
            aggregationLabels.includes(String(value)),
    );

    if (selectedTheme !== undefined && selectedTheme !== null) {
        return String(selectedTheme);
    }

    return fallbackTitle ?? "Drilldown";
};

export const resolveViewDataClassification = ({
    configuredClassification,
    canViewDrivers,
}: {
    configuredClassification?: ViewDataClassification;
    canViewDrivers: boolean;
}): ViewDataClassification => {
    if (!canViewDrivers) return "topics";
    return configuredClassification ?? "drivers";
};

export const normalizeEndpointForDrilldown = (
    endpoint?: SignalDataEndpoint | string,
): SignalDataEndpoint | undefined => {
    if (!endpoint) return undefined;
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedGroupedModelResultsId,
        )
    ) {
        return SignalsEndpoints.additionalReports.PublishedGroupedModelResultsId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsOverTimeId,
        )
    ) {
        return SignalsEndpoints.additionalReports
            .PublishedModelResultsOverTimeId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.PublishedModelResultsCountId,
        )
    ) {
        return SignalsEndpoints.additionalReports.PublishedModelResultsCountId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.ModelResultsOverTimeId,
        )
    ) {
        return SignalsEndpoints.additionalReports.ModelResultsOverTimeId;
    }
    if (
        endpoint.includes(
            SignalsEndpoints.additionalReports.ModelResultsCountId,
        )
    ) {
        return SignalsEndpoints.additionalReports.ModelResultsCountId;
    }
    return endpoint as SignalDataEndpoint;
};

export const hasSelectedIds = (
    source: DrilldownSource,
): source is DrilldownSource & {
    selectedIds: NonNullable<DrilldownSource["selectedIds"]>;
} => !!source.selectedIds;

export const hasTopics = (
    source: DrilldownSource,
): source is DrilldownSource & {
    topics: NonNullable<DrilldownSource["topics"]>;
} => Array.isArray(source.topics);

export const hasThemes = (
    source: DrilldownSource,
): source is DrilldownSource & {
    themes: NonNullable<DrilldownSource["themes"]>;
} => Array.isArray(source.themes);

export const hasParsedVizOptions = (
    source: DrilldownSource,
): source is DrilldownSource & {
    parsedVizOptions: NonNullable<DrilldownSource["parsedVizOptions"]>;
} => !!source.parsedVizOptions;

export const hasData = (
    source: DrilldownSource,
): source is DrilldownSource & {
    data: NonNullable<DrilldownSource["data"]>;
} => Array.isArray(source.data);

export const hasTemporalGrain = (
    source: DrilldownSource,
): source is DrilldownSource & {
    temporalGrain: NonNullable<DrilldownSource["temporalGrain"]>;
} => !!source.temporalGrain;

export const hasCapability = (
    source: DrilldownSource,
    capability: DrilldownCapability,
) => {
    if (capability.startsWith("selectedIds.")) {
        if (!hasSelectedIds(source)) return false;
        const key = capability.split(".")[1];
        const value = source.selectedIds[key];
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null;
    }
    switch (capability) {
        case "topics":
            return hasTopics(source);
        case "themes":
            return hasThemes(source);
        case "parsedVizOptions":
            return hasParsedVizOptions(source);
        case "data":
            return hasData(source);
        case "temporalGrain":
            return hasTemporalGrain(source);
        default:
            return true;
    }
};

export const findMissingCapabilities = (
    strategy: DrilldownBehavior | undefined,
    source?: DrilldownSource,
) => {
    if (!strategy?.needs || !source) return [];
    return strategy.needs.filter((need) => !hasCapability(source, need));
};

/** The subset of a drilldown row the Evaluate flow reads to decide what can be done with it. */
export type EvaluableRow = {
    isReadyToSample?: boolean;
    evaluationId?: string | null;
    evalType?: string | null;
};

/** A row already scored by the AI, i.e. the evaluation sitting on it is a MOTF one. */
export const isRowAiEvaluated = (row: EvaluableRow) =>
    !!row.evaluationId && row.evalType === EvalType[EvalType.MiddleOfTheFunnel];

/** A row with no evaluation at all, so the Evaluate flow has to sample it first. */
export const isRowUnevaluated = (row: EvaluableRow) => !row.evaluationId;

/**
 * Rows the Evaluate flow accepts: unevaluated conversations that are ready to sample, and
 * conversations the AI has already evaluated. Conversations a human evaluated are excluded --
 * AUT-10592 covers AI and unevaluated only, and reassigning a human's evaluation is the
 * Assigned Interactions flow's job.
 */
export const isRowEvaluable = (row: EvaluableRow) =>
    isRowUnevaluated(row) ? !!row.isReadyToSample : isRowAiEvaluated(row);

/** One entry of the sampler's reply: a "Fail" result means that conversation was refused. */
export type OnDemandSampleResult = {
    result?: string;
    amdId?: string;
    message?: string;
    evaluationId?: string;
};

/** What became of each conversation in one Evaluate action. */
export type EvaluateOutcome = {
    /** Rows confirmed done. They leave the selection so a retry cannot repeat them. */
    succeededRowIds: GridRowId[];
    /** Rows the server refused. Safe to try again, so they stay selected. */
    failed: number;
    /** Rows whose request never came back, so the work may or may not have happened. */
    unconfirmed: number;
    unconfirmedRowIds: GridRowId[];
    conflictedRowIds: GridRowId[];
    processingPending: number;
};

export const emptyEvaluateOutcome = (): EvaluateOutcome => ({
    succeededRowIds: [],
    failed: 0,
    unconfirmed: 0,
    unconfirmedRowIds: [],
    conflictedRowIds: [],
    processingPending: 0,
});

/** Only an explicit success confirms creation. Missing or contradictory replies are unresolved. */
export const partitionSampleOutcome = (
    batch: { id: GridRowId; audioMetadataId?: string }[],
    response: OnDemandSampleResult[] | undefined,
): EvaluateOutcome => batch.reduce((outcome, row) => {
    const results = (Array.isArray(response) ? response : [])
        .filter((result) => row.audioMetadataId && result?.amdId === row.audioMetadataId);
    if (results.length === 1 && results[0].result === "Success") {
        outcome.succeededRowIds.push(row.id);
    } else if (results.length === 1 && results[0].result === "Fail") {
        outcome.failed += 1;
    } else if (results.length === 1 && results[0].result === "Conflict") {
        outcome.conflictedRowIds.push(row.id);
    } else {
        outcome.unconfirmed += 1;
        outcome.unconfirmedRowIds.push(row.id);
        if (results.some((result) => result.result === "ProcessingPending")) {
            outcome.processingPending += 1;
        }
    }
    return outcome;
}, emptyEvaluateOutcome());

/** Adds up the outcomes of the separate routes one Evaluate action can take. */
export const mergeEvaluateOutcomes = (
    ...outcomes: EvaluateOutcome[]
): EvaluateOutcome =>
    outcomes.reduce(
        (merged, outcome) => ({
            succeededRowIds: [
                ...merged.succeededRowIds,
                ...outcome.succeededRowIds,
            ],
            failed: merged.failed + outcome.failed,
            unconfirmed: merged.unconfirmed + outcome.unconfirmed,
            unconfirmedRowIds: [...merged.unconfirmedRowIds, ...outcome.unconfirmedRowIds],
            conflictedRowIds: [...merged.conflictedRowIds, ...outcome.conflictedRowIds],
            processingPending: merged.processingPending + outcome.processingPending,
        }),
        emptyEvaluateOutcome(),
    );
